import { Icon, Icons } from '@starrez/starryui';
import { Button, color, HStack, VStack } from '@starrez/starryui9';
import { useSentry } from '@starrez/starryutils';
import React, { useState } from 'react';
import { Form } from 'react-final-form';
import styled from 'styled-components';
import { Fieldset } from '../../../../../.starryUI/Components/Fieldset';
import { FolderDropdownField } from '../../../../../.starryUI/controls/FolderDropdownField';
import { RadioButton, RadioButtonItem } from '../../../../../.starryUI/controls/RadioButton';
import { TextInputField } from '../../../../../.starryUI/controls/TextInputField';
import { ITreeData } from '../../../../../.starryUI/controls/Tree/types';
import { useManagedDropdown } from '../../../../../.starryUI/ManagedDropdown/ManagedDropdown';
import { useMainDirectoryListQuery } from '../../graphql/useAllMainDirectoryListQuery';
import { useUpdateMainDirectoryWorkspace } from '../../graphql/useUpdateMainDirectoryWorkspace';
import { strings } from '../../localization';
import { filterStorageName, gridStorageName } from '../../pages/EntryDirectory';
import { GetViewType } from '../../utils/typeUtils';
import { useSaveView } from './context/SaveViewContext';

declare let SEND_TO_SENTRY: boolean;

type SaveEnum = 'SAVE' | 'SAVE_AS';

interface IErrors {
    name?: string;
    folder?: string;
}

interface ISaveResults {
    name: string;
    folder: ITreeData;
}

export const SaveForm = () => {
    const { closeDropdown } = useManagedDropdown();
    const { captureException } = useSentry();

    const [updateDynamicList] = useUpdateMainDirectoryWorkspace();
    const { onSave, currentView } = useSaveView();

    const unsavable = currentView?.listType ? GetViewType(currentView.listType) === 'NONE' : true;
    const [saveType, setSaveType] = useState<SaveEnum>(unsavable ? 'SAVE_AS' : 'SAVE');

    const { data, error } = useMainDirectoryListQuery({
        filter: [{ folder: { eq: true } }, { listType: { eq: 'DYNAMIC' } }],
        skip: 0,
        sort: [
            { field: 'PARENT_ID', order: 'ASC' },
            { field: 'DESCRIPTION', order: 'ASC' },
        ],
    });

    const handleSaveView = (e: ISaveResults) => {
        const { name, folder } = e;

        const gridFormat = JSON.parse(sessionStorage.getItem(gridStorageName) || '{}');
        const filters = JSON.parse(sessionStorage.getItem(filterStorageName) || '{}')?.filters || [];

        if (Object.keys(gridFormat).length === 0) {
            closeDropdown();
            return;
        }

        const getParams = () => {
            switch (saveType) {
                case 'SAVE':
                    return { id: Number(currentView?.id) };
                case 'SAVE_AS':
                    return { description: name, parentID: Number(folder?.id) };
            }
        };

        updateDynamicList({
            variables: {
                params: {
                    ...getParams(),
                    saveOption: saveType,
                    gridFormat,
                    filters,
                },
            },
        }).then(({ data }) => {
            if (data?.updateMainDirectoryWorkspace) {
                onSave(data.updateMainDirectoryWorkspace.id);
            }
        });

        closeDropdown();
    };

    const handleSaveType = (type: SaveEnum) => {
        setSaveType(type);
    };

    const buildTreeData = () => {
        if (data?.allMainDirectoryList?.nodes?.length) {
            return data.allMainDirectoryList.nodes.map((folder) => ({
                id: folder.id,
                caption: folder.caption,
                parentId: folder.parentID.toString(),
                isFolder: folder.folder,
            }));
        }

        return [];
    };

    if (SEND_TO_SENTRY && error) {
        captureException(error);
    }

    const fieldsDisabled = saveType === 'SAVE';

    return (
        <Form
            onSubmit={handleSaveView}
            initialValues={{
                name: undefined,
                folder: undefined, // enter it here.
            }}
            validate={(values) => {
                const errors: IErrors = {};
                if (!fieldsDisabled && !values.name) {
                    errors.name = strings.SaveView_Message_PleaseEnterAName;
                }
                if (!fieldsDisabled && !values.folder) {
                    errors.folder = strings.SaveView_Message_PleaseSelectAFolder;
                }
                return errors;
            }}
            render={({ handleSubmit }) => (
                <form onSubmit={handleSubmit}>
                    <CloseButton type="button" aria-label={strings.Action_Close} title={strings.Action_Close} onClick={closeDropdown}>
                        <Icon Icon={Icons.CloseThin} />
                    </CloseButton>
                    <VStack pad="medium" gap="small">
                        <Fieldset caption={strings.SaveView_Label_SaveViewAsDynamicList}>
                            <RadioButton compact value={unsavable ? 'SAVE_AS' : 'SAVE'} onChange={(type) => handleSaveType(type as SaveEnum)} stackOrientation="Vertical">
                                <RadioButtonItem caption={strings.SaveView_Label_Save} value={'SAVE'} disabled={unsavable}/>
                                <RadioButtonItem caption={strings.SaveView_Label_SaveAsNew} value={'SAVE_AS'} />
                            </RadioButton>
                        </Fieldset>

                        <TextInputField name="name" label={strings.SaveView_Label_Name} disabled={fieldsDisabled} />

                        <FolderDropdownField
                            allowSearch
                            nodes={buildTreeData()}
                            name="folder"
                            label={strings.SaveView_Label_Folder}
                            disabled={fieldsDisabled}
                        />

                        <Divider />

                        <HStack gap="xSmall" justifyContent="end">
                            <Button variant="Ghost" type="button" onClick={closeDropdown}>
                                {strings.Action_Cancel}
                            </Button>
                            <Button variant="Primary" type="submit">
                                {strings.Action_Save}
                            </Button>
                        </HStack>
                    </VStack>
                </form>
            )}
        />
    );
};

const CloseButton = styled.button`
    position: absolute;
    top: 20px;
    right: 16px;
    padding: 0;
    border: 0;
    background: transparent;
    cursor: pointer;
    color: ${color.Grey.G70};

    &:hover {
        color: ${color.Grey.G80};
    }
    
`;

const Divider = styled.hr`
    height: 1px;
    background: ${color.Grey.G20};
    border: 0;
    width: 100%;
`;
